import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";
import FollowerModal from "../components/FollowerModal";
import RecipeCard from "../components/RecipeCard";
import { getApiErrorMessage } from "../lib/apiErrors";
import { toast } from "../lib/toast";

const Profile = () => {
  const { id } = useParams();
  const { currentUser, refreshUser } = useAuth();
  const isOwnProfile = !id || (currentUser && String(id) === String(currentUser.id));

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [cartRecipeIds, setCartRecipeIds] = useState([]);

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [hoveringFollow, setHoveringFollow] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Follower/following modal
  const [followerModal, setFollowerModal] = useState(null); // "followers" | "following" | null

  // Edit profile state
  const [editing, setEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editDietPreference, setEditDietPreference] = useState("");
  const [saving, setSaving] = useState(false);

  // Profile picture upload
  const avatarInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const profileUserId = isOwnProfile ? currentUser?.id : id;

  const fetchCartIds = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/cart/");
      setCartRecipeIds((res.data.items || []).map((item) => item.recipe.id));
    } catch {
      setCartRecipeIds([]);
    }
  }, []);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/api/users/${profileUserId}/profile/`);
        setProfile(res.data);
        if (!isOwnProfile) {
          setIsFollowing(res.data.is_following || false);
        }
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      } finally {
        setLoading(false);
      }
    };

    if (profileUserId) {
      fetchProfile();
    }
  }, [id, isOwnProfile, profileUserId]);

  // Reset tab when navigating to a different profile
  useEffect(() => {
    setActiveTab("recipes");
    setPage(1);
  }, [id]);

  useEffect(() => {
    fetchCartIds();
    const handler = () => fetchCartIds();
    window.addEventListener("cart-changed", handler);
    return () => window.removeEventListener("cart-changed", handler);
  }, [fetchCartIds]);

  // Fetch recipes for active tab
  useEffect(() => {
    const fetchRecipes = async () => {
      if (!profileUserId) return;
      setRecipesLoading(true);
      try {
        let params = { page };

        if (activeTab === "recipes") {
          params.owner = profileUserId;
        } else if (activeTab === "liked") {
          params.visibility = "liked";
        } else if (activeTab === "forked") {
          params.visibility = "forked";
        } else if (activeTab === "bookmarks") {
          params.visibility = "bookmarks";
        }

        const res = await axiosInstance.get("/api/recipes/", { params });
        setRecipes(res.data.results || []);
        setTotalCount(res.data.count || 0);
        setHasNext(!!res.data.next);
        setHasPrevious(!!res.data.previous);
      } catch (err) {
        console.error("Failed to fetch recipes:", err);
      } finally {
        setRecipesLoading(false);
      }
    };

    fetchRecipes();
  }, [activeTab, page, profileUserId]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      await axiosInstance.post(`/api/users/${id}/follow/`);
      setIsFollowing(true);
      setProfile((prev) => prev ? { ...prev, followers_count: (prev.followers_count || 0) + 1 } : prev);
    } catch (err) {
      if (err.response?.status === 409) {
        setIsFollowing(true);
      }
      console.error("Failed to follow:", err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    setFollowLoading(true);
    try {
      await axiosInstance.delete(`/api/users/${id}/follow/`);
      setIsFollowing(false);
      setProfile((prev) => prev ? { ...prev, followers_count: Math.max((prev.followers_count || 1) - 1, 0) } : prev);
    } catch (err) {
      console.error("Failed to unfollow:", err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleEditStart = () => {
    setEditFirstName(profile?.first_name || "");
    setEditLastName(profile?.last_name || "");
    setEditBio(profile?.bio || "");
    setEditLocation(profile?.location || "");
    setEditDietPreference(profile?.diet_preference || "");
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch("/api/user/profile/", {
        first_name: editFirstName,
        last_name: editLastName,
        bio: editBio,
        location: editLocation,
        diet_preference: editDietPreference,
      });
      // Refetch full profile with stats
      const refreshed = await axiosInstance.get(`/api/users/${profileUserId}/profile/`);
      setProfile(refreshed.data);
      setEditing(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error("Failed to update profile:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarClick = () => {
    if (isOwnProfile) {
      avatarInputRef.current?.click();
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const data = new FormData();
      data.append("profile_picture", file);
      await axiosInstance.patch("/api/user/profile/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refetch full profile with stats
      const refreshed = await axiosInstance.get(`/api/users/${profileUserId}/profile/`);
      setProfile(refreshed.data);
      if (refreshUser) refreshUser();
      toast.success("Profile picture updated.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to upload profile picture."));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile?.first_name || "User";

  const tabs = isOwnProfile
    ? [
        { key: "recipes", label: "Recipes" },
        { key: "liked", label: "Liked" },
        { key: "forked", label: "Forked" },
        { key: "bookmarks", label: "Bookmarked" },
      ]
    : [{ key: "recipes", label: "Recipes" }];

  if (loading) {
    return <div className="type-muted p-6">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="type-muted p-6">Profile not found</div>;
  }

  return (
    <div className="flex-1 p-6 max-w-4xl mx-auto">
      {/* Hidden file input for avatar upload */}
      <input
        type="file"
        ref={avatarInputRef}
        onChange={handleAvatarUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Back to My Profile link */}
      {!isOwnProfile && (
        <Link to="/profile" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 mb-4">
          ← My Profile
        </Link>
      )}

      {/* Profile Header */}
      <div className="flex items-start space-x-6 mb-8">
        {/* Avatar */}
        <div
          className={`relative flex-shrink-0 ${isOwnProfile ? "cursor-pointer group" : ""}`}
          onClick={handleAvatarClick}
        >
          <Avatar user={profile} size="xl" />
          {isOwnProfile && (
            <div className="absolute inset-0 w-24 h-24 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
              <span className="text-white text-xs font-medium">
                {uploadingAvatar ? "..." : "Edit"}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          {editing ? (
            /* Edit Form */
            <div className="space-y-3">
              <div className="flex space-x-3">
                <div className="w-full">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    placeholder="First name"
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
                <div className="w-full">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    placeholder="Last name"
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <div className="w-full">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Location</label>
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="e.g., Austin, TX"
                    maxLength={100}
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
                <div className="w-full">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Diet Preference</label>
                  <input
                    type="text"
                    value={editDietPreference}
                    onChange={(e) => setEditDietPreference(e.target.value)}
                    placeholder="e.g., Vegan, Keto"
                    maxLength={50}
                    className="border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Bio</label>
                <textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  placeholder="Write a bio..."
                  rows={3}
                  maxLength={300}
                  className="border rounded-lg px-3 py-2 text-sm w-full resize-none focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleEditSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleEditCancel}
                  className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Display mode */
            <>
              <div className="flex items-center justify-between">
                <h1 className="type-page-title truncate">{displayName}</h1>
                {isOwnProfile ? (
                  <button
                    onClick={handleEditStart}
                    className="px-4 py-1.5 rounded-full text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition flex-shrink-0"
                  >
                    Edit Profile
                  </button>
                ) : (
                  /* Follow/Unfollow button */
                  isFollowing ? (
                    <button
                      onMouseEnter={() => setHoveringFollow(true)}
                      onMouseLeave={() => setHoveringFollow(false)}
                      onClick={handleUnfollow}
                      disabled={followLoading}
                      className={`px-6 py-1.5 rounded-full text-sm font-semibold border transition flex-shrink-0 ${
                        hoveringFollow
                          ? "border-red-300 text-red-500 bg-red-50 dark:bg-red-900/30"
                          : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {hoveringFollow ? "Unfollow" : "Following"}
                    </button>
                  ) : (
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="px-6 py-1.5 rounded-full text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition flex-shrink-0"
                    >
                      Follow
                    </button>
                  )
                )}
              </div>

              {profile.bio && (
                <p className="type-body mt-5 leading-relaxed">{profile.bio}</p>
              )}

              {/* Location, diet, joined date */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5 text-xs text-gray-500 dark:text-gray-400">
                {profile.location && (
                  <span>📍 <span className="font-semibold text-gray-600 dark:text-gray-300">Location:</span> {profile.location}</span>
                )}
                {profile.diet_preference && (
                  <span>🥗 <span className="font-semibold text-gray-600 dark:text-gray-300">Preference:</span> {profile.diet_preference}</span>
                )}
                {profile.date_joined && (
                  <span>📅 <span className="font-semibold text-gray-600 dark:text-gray-300">Joined:</span> {new Date(profile.date_joined).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
                )}
              </div>

            </>
          )}

          {/* Stats Row */}
          <div className="flex items-center space-x-6 mt-6">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-bold text-gray-800 dark:text-white">{profile.recipes_count ?? 0}</span> recipes
            </span>
            <button
              onClick={() => setFollowerModal("followers")}
              className="text-sm text-gray-700 dark:text-gray-300 hover:underline"
            >
              <span className="font-bold text-gray-800 dark:text-white">{profile.followers_count ?? 0}</span> followers
            </button>
            <button
              onClick={() => setFollowerModal("following")}
              className="text-sm text-gray-700 dark:text-gray-300 hover:underline"
            >
              <span className="font-bold text-gray-800 dark:text-white">{profile.following_count ?? 0}</span> following
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b dark:border-gray-700 mb-6">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-semibold transition border-b-2 ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Recipe Grid */}
      {recipesLoading ? (
        <p className="type-muted text-center py-12">Loading recipes...</p>
      ) : recipes.length === 0 ? (
        <p className="type-muted text-center py-12">
          {activeTab === "recipes"
            ? "No recipes yet"
            : activeTab === "liked"
            ? "No liked recipes yet"
            : activeTab === "forked"
            ? "No forked recipes yet"
            : "No bookmarked recipes yet"}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {recipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} cartRecipeIds={cartRecipeIds} />
            ))}
          </div>

          {/* Pagination */}
          {Math.ceil(totalCount / 12) > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={!hasPrevious}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {Math.ceil(totalCount / 12)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Follower/Following Modal */}
      {followerModal && (
        <FollowerModal
          userId={profileUserId}
          type={followerModal}
          onClose={() => setFollowerModal(null)}
        />
      )}
    </div>
  );
};

export default Profile;
